import { supabase } from '../lib/supabase';
import { renderEmailEvent } from './emailTemplates';

/**
 * Notification Service
 * Handles notification management operations
 * Converts between snake_case (database) and camelCase (React)
 */

export const notificationService = {
  /**
   * Get the custom template for a single automated event, or null if the
   * conference hasn't customized it (caller falls back to the built-in default).
   */
  async getEventTemplate(conferenceId, eventKey) {
    try {
      if (!conferenceId || !eventKey) return null;
      const { data, error } = await supabase
        ?.from('notification_templates')
        ?.select('id, name, subject, message, event_key')
        ?.eq('conference_id', conferenceId)
        ?.eq('event_key', eventKey)
        ?.maybeSingle();

      if (error) throw error;
      if (!data) return null;
      return {
        id: data?.id,
        name: data?.name,
        subject: data?.subject,
        message: data?.message,
        eventKey: data?.event_key
      };
    } catch (error) {
      console.error('Get event template error:', error);
      return null;
    }
  },

  /**
   * Get all customized automated-event templates for a conference, keyed by event.
   */
  async getEventTemplates(conferenceId) {
    try {
      if (!conferenceId) return {};
      const { data, error } = await supabase
        ?.from('notification_templates')
        ?.select('id, name, subject, message, event_key')
        ?.eq('conference_id', conferenceId)
        ?.not('event_key', 'is', null);

      if (error) throw error;

      const byKey = {};
      data?.forEach(row => {
        byKey[row?.event_key] = {
          id: row?.id,
          name: row?.name,
          subject: row?.subject,
          message: row?.message,
          eventKey: row?.event_key
        };
      });
      return byKey;
    } catch (error) {
      console.error('Get event templates error:', error);
      return {};
    }
  },

  /**
   * Create or update the custom template for an automated event.
   */
  async upsertEventTemplate(conferenceId, eventKey, { name, subject, message }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('notification_templates')
        ?.upsert({
          conference_id: conferenceId,
          event_key: eventKey,
          created_by: user?.id,
          name: name || eventKey,
          notification_type: 'custom',
          subject: subject || '',
          message: message || '',
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'conference_id,event_key' })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Upsert event template error:', error);
      throw error;
    }
  },

  /**
   * Remove a conference's customization for an event (reverts to built-in default).
   */
  async resetEventTemplate(conferenceId, eventKey) {
    try {
      const { error } = await supabase
        ?.from('notification_templates')
        ?.delete()
        ?.eq('conference_id', conferenceId)
        ?.eq('event_key', eventKey);

      if (error) throw error;
    } catch (error) {
      console.error('Reset event template error:', error);
      throw error;
    }
  },

  /**
   * Render an automated event using the conference's custom template (if any),
   * falling back to the built-in default, then send it via Resend.
   * Returns null without sending when conferenceId/eventKey are missing.
   */
  async sendEventEmail(conferenceId, eventKey, recipientEmail, vars = {}, emailType = 'notification') {
    if (!recipientEmail || !eventKey) return null;
    const customTemplate = conferenceId ? await this.getEventTemplate(conferenceId, eventKey) : null;
    const rendered = renderEmailEvent(eventKey, customTemplate, vars);
    if (!rendered) return null;
    return await this.sendEmail(recipientEmail, rendered?.subject, rendered?.message, emailType);
  },
  /**
   * Send an email whose text has already been composed.
   *
   * `sendEventEmail` renders a template at send time. That's right for
   * automated mail, but wrong when a human has just read and edited the exact
   * words on screen — re-rendering would silently discard their edits. This
   * sends the text as given.
   */
  async sendCustomEmail({ to, subject, message, emailType = 'decision' } = {}) {
    if (!to || !subject || !message) return null;
    return await this.sendEmail(to, subject, message, emailType);
  },

  /**
   * Get notification templates for a conference
   */
  async getTemplates(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('notification_templates')
        ?.select('*')
        ?.eq('conference_id', conferenceId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(template => ({
        id: template?.id,
        conferenceId: template?.conference_id,
        createdBy: template?.created_by,
        name: template?.name,
        notificationType: template?.notification_type,
        subject: template?.subject,
        message: template?.message,
        isDefault: template?.is_default,
        createdAt: template?.created_at,
        updatedAt: template?.updated_at
      })) || [];
    } catch (error) {
      console.error('Get templates error:', error);
      throw error;
    }
  },

  /**
   * Create notification template
   */
  async createTemplate(templateData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('notification_templates')
        ?.insert({
          conference_id: templateData?.conferenceId,
          created_by: user?.id,
          name: templateData?.name,
          notification_type: templateData?.notificationType,
          subject: templateData?.subject,
          message: templateData?.message,
          is_default: templateData?.isDefault || false
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { id: data?.id };
    } catch (error) {
      console.error('Create template error:', error);
      throw error;
    }
  },

  /**
   * Update notification template
   */
  async updateTemplate(templateId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('notification_templates')
        ?.update({
          name: updates?.name,
          notification_type: updates?.notificationType,
          subject: updates?.subject,
          message: updates?.message,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', templateId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update template error:', error);
      throw error;
    }
  },

  /**
   * Delete notification template
   */
  async deleteTemplate(templateId) {
    try {
      const { error } = await supabase
        ?.from('notification_templates')
        ?.delete()
        ?.eq('id', templateId);

      if (error) throw error;
    } catch (error) {
      console.error('Delete template error:', error);
      throw error;
    }
  },

  /**
   * Get notifications for a conference
   */
  async getNotifications(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select(`
          *,
          user_profiles!notifications_created_by_fkey (first_name, last_name, email)
        `)
        ?.eq('conference_id', conferenceId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(notification => ({
        id: notification?.id,
        conferenceId: notification?.conference_id,
        templateId: notification?.template_id,
        createdBy: notification?.created_by,
        createdByName: notification?.user_profiles ? `${notification?.user_profiles?.first_name} ${notification?.user_profiles?.last_name}` : 'Unknown',
        notificationType: notification?.notification_type,
        subject: notification?.subject,
        message: notification?.message,
        recipientType: notification?.recipient_type,
        recipientFilter: notification?.recipient_filter,
        status: notification?.status,
        scheduledAt: notification?.scheduled_at,
        sentAt: notification?.sent_at,
        totalRecipients: notification?.total_recipients,
        successfulSends: notification?.successful_sends,
        failedSends: notification?.failed_sends,
        createdAt: notification?.created_at,
        updatedAt: notification?.updated_at
      })) || [];
    } catch (error) {
      console.error('Get notifications error:', error);
      throw error;
    }
  },

  /**
   * Get potential recipients based on filter
   */
  async getRecipients(conferenceId, recipientType, recipientFilter = {}) {
    try {
      let query;

      switch (recipientType) {
        case 'all_reviewers':
          query = supabase
            ?.from('conference_roles')
            ?.select(`
              user_id,
              user_profiles (id, first_name, last_name, email, institution)
            `)
            ?.eq('conference_id', conferenceId)
            ?.eq('role', 'reviewer');
          break;

        case 'all_authors':
          query = supabase
            ?.from('conference_submissions')
            ?.select(`
              user_id,
              user_profiles (id, first_name, last_name, email, institution)
            `)
            ?.eq('conference_id', conferenceId);
          break;

        case 'paper_reviewers':
          if (!recipientFilter?.submissionId) {
            throw new Error('Submission ID required for paper reviewers');
          }
          query = supabase
            ?.from('submission_reviews')
            ?.select(`
              reviewer_id,
              user_profiles (id, first_name, last_name, email, institution)
            `)
            ?.eq('submission_id', recipientFilter?.submissionId);
          break;

        case 'paper_authors':
          if (!recipientFilter?.submissionId) {
            throw new Error('Submission ID required for paper authors');
          }
          query = supabase
            ?.from('conference_submissions')
            ?.select(`
              user_id,
              user_profiles (id, first_name, last_name, email, institution)
            `)
            ?.eq('id', recipientFilter?.submissionId);
          break;

        case 'role_based':
          if (!recipientFilter?.role) {
            throw new Error('Role required for role-based recipients');
          }
          query = supabase
            ?.from('conference_roles')
            ?.select(`
              user_id,
              user_profiles (id, first_name, last_name, email, institution)
            `)
            ?.eq('conference_id', conferenceId)
            ?.eq('role', recipientFilter?.role);
          break;

        // Everyone holding any role in this conference. Used to populate the
        // people-picker, so a chair can address one named person — the service
        // could already send to specific users, but nothing in the interface
        // ever offered it.
        case 'everyone':
          query = supabase
            ?.from('conference_roles')
            ?.select(`
              user_id,
              user_profiles (id, first_name, last_name, email, institution)
            `)
            ?.eq('conference_id', conferenceId);
          break;

        case 'individuals':      // what the send form calls it
        case 'specific_users':
          if (!recipientFilter?.userIds || recipientFilter?.userIds?.length === 0) {
            // An empty selection isn't an error — it's just nobody yet.
            return [];
          }
          query = supabase
            ?.from('user_profiles')
            ?.select('id, first_name, last_name, email, institution')
            ?.in('id', recipientFilter?.userIds);
          break;

        default:
          throw new Error('Invalid recipient type');
      }

      const { data, error } = await query;
      if (error) throw error;

      // Deduplicate recipients by user ID
      const uniqueRecipients = new Map();
      data?.forEach(item => {
        const profile = item?.user_profiles || item;
        if (profile?.id && !uniqueRecipients?.has(profile?.id)) {
          uniqueRecipients?.set(profile?.id, {
            id: profile?.id,
            firstName: profile?.first_name,
            lastName: profile?.last_name,
            email: profile?.email,
            institution: profile?.institution
          });
        }
      });

      return Array.from(uniqueRecipients?.values());
    } catch (error) {
      console.error('Get recipients error:', error);
      throw error;
    }
  },

  /**
   * Create and send notification
   */
  async createNotification(notificationData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get recipients
      const recipients = await this.getRecipients(
        notificationData?.conferenceId,
        notificationData?.recipientType,
        notificationData?.recipientFilter
      );

      if (recipients?.length === 0) {
        throw new Error('No recipients found for the selected criteria');
      }

      const notificationId = globalThis?.crypto?.randomUUID?.();
      if (!notificationId) {
        throw new Error('Unable to generate notification ID');
      }

      // Create notification record
      const { error: notificationError } = await supabase
        ?.from('notifications')
        ?.insert({
          id: notificationId,
          conference_id: notificationData?.conferenceId,
          template_id: notificationData?.templateId || null,
          created_by: user?.id,
          notification_type: notificationData?.notificationType,
          subject: notificationData?.subject,
          message: notificationData?.message,
          recipient_type: notificationData?.recipientType,
          recipient_filter: notificationData?.recipientFilter || {},
          status: notificationData?.sendImmediately ? 'sent' : 'draft',
          scheduled_at: notificationData?.scheduledAt || null,
          sent_at: notificationData?.sendImmediately ? new Date()?.toISOString() : null,
          total_recipients: recipients?.length,
          successful_sends: notificationData?.sendImmediately ? recipients?.length : 0,
          failed_sends: 0
        });

      if (notificationError) throw notificationError;

      // Create recipient records
      const recipientRecords = recipients?.map(recipient => ({
        notification_id: notificationId,
        user_id: recipient?.id,
        status: notificationData?.sendImmediately ? 'sent' : 'pending',
        sent_at: notificationData?.sendImmediately ? new Date()?.toISOString() : null
      }));

      const { error: recipientsError } = await supabase
        ?.from('notification_recipients')
        ?.insert(recipientRecords);

      if (recipientsError) throw recipientsError;

      return {
        id: notificationId,
        recipientCount: recipients?.length
      };
    } catch (error) {
      console.error('Create notification error:', error);
      throw error;
    }
  },

  /**
   * Get notification statistics for a conference
   */
  async getNotificationStats(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('status, total_recipients, successful_sends, failed_sends')
        ?.eq('conference_id', conferenceId);

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        sent: data?.filter(n => n?.status === 'sent')?.length || 0,
        draft: data?.filter(n => n?.status === 'draft')?.length || 0,
        scheduled: data?.filter(n => n?.status === 'scheduled')?.length || 0,
        totalRecipients: data?.reduce((sum, n) => sum + (n?.total_recipients || 0), 0),
        successfulSends: data?.reduce((sum, n) => sum + (n?.successful_sends || 0), 0),
        failedSends: data?.reduce((sum, n) => sum + (n?.failed_sends || 0), 0)
      };

      return stats;
    } catch (error) {
      console.error('Get notification stats error:', error);
      throw error;
    }
  },

  /**
   * Get all papers for a conference (for recipient filtering)
   */
  async getPapers(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_submissions')
        ?.select(`
          id,
          title,
          user_profiles (first_name, last_name)
        `)
        ?.eq('conference_id', conferenceId)
        ?.eq('status', 'submitted')
        ?.order('title', { ascending: true });

      if (error) throw error;

      return data?.map(paper => ({
        id: paper?.id,
        title: paper?.title,
        authorName: paper?.user_profiles ? `${paper?.user_profiles?.first_name} ${paper?.user_profiles?.last_name}` : 'Unknown'
      })) || [];
    } catch (error) {
      console.error('Get papers error:', error);
      throw error;
    }
  },

  /**
   * Get notifications for the current user (in-app)
   */
  async getUserNotifications(limit = 20) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const user = session?.user;
      if (!user) return [];

      // Try with join first
      const { data, error } = await supabase
        ?.from('notification_recipients')
        ?.select(`
          id,
          status,
          read_at,
          created_at,
          notification_id,
          notifications (
            id,
            subject,
            message,
            notification_type,
            conference_id,
            created_at
          )
        `)
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) {
        // Fallback: fetch recipients without joining notifications (avoids RLS recursion)
        if (error?.message?.includes('recursion') || error?.code === '42P17') {
          console.debug('Notification RLS recursion — run the latest migration to fix this');
        }
        const { data: recipients, error: recError } = await supabase
          ?.from('notification_recipients')
          ?.select('id, status, read_at, created_at, notification_id')
          ?.eq('user_id', user?.id)
          ?.order('created_at', { ascending: false })
          ?.limit(limit);

        if (recError || !recipients?.length) return [];

        // Fetch notification details individually
        const notifIds = [...new Set(recipients?.map(r => r?.notification_id)?.filter(Boolean))];
        const { data: notifs } = await supabase
          ?.from('notifications')
          ?.select('id, subject, message, notification_type, conference_id, created_at')
          ?.in('id', notifIds);

        const notifMap = {};
        notifs?.forEach(n => { notifMap[n?.id] = n; });

        return recipients?.map(item => {
          const notif = notifMap?.[item?.notification_id];
          return {
            id: item?.id,
            notificationId: item?.notification_id,
            subject: notif?.subject || 'Notification',
            message: notif?.message || '',
            type: notif?.notification_type,
            conferenceId: notif?.conference_id,
            status: item?.status,
            isRead: !!item?.read_at,
            readAt: item?.read_at,
            createdAt: notif?.created_at || item?.created_at
          };
        }) || [];
      }

      return data?.map(item => ({
        id: item?.id,
        notificationId: item?.notifications?.id,
        subject: item?.notifications?.subject,
        message: item?.notifications?.message,
        type: item?.notifications?.notification_type,
        conferenceId: item?.notifications?.conference_id,
        status: item?.status,
        isRead: !!item?.read_at,
        readAt: item?.read_at,
        createdAt: item?.notifications?.created_at || item?.created_at
      })) || [];
    } catch (error) {
      console.error('Get user notifications error:', error);
      return [];
    }
  },

  /**
   * Get unread notification count for current user
   */
  async getUnreadCount() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const user = session?.user;
      if (!user) return 0;

      const { count, error } = await supabase
        ?.from('notification_recipients')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', user?.id)
        ?.is('read_at', null);

      if (error) {
        if (error?.message?.includes('recursion') || error?.code === '42P17') {
          console.debug('Notification count RLS recursion — run the latest migration to fix');
        }
        return 0;
      }
      return count || 0;
    } catch {
      return 0;
    }
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(recipientId) {
    try {
      const { error } = await supabase
        ?.from('notification_recipients')
        ?.update({ read_at: new Date()?.toISOString() })
        ?.eq('id', recipientId);

      if (error) throw error;
    } catch (error) {
      console.error('Mark as read error:', error);
    }
  },

  /**
   * Mark all notifications as read for current user
   */
  async markAllAsRead() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const { error } = await supabase
        ?.from('notification_recipients')
        ?.update({ read_at: new Date()?.toISOString() })
        ?.eq('user_id', user?.id)
        ?.is('read_at', null);

      if (error) throw error;
    } catch (error) {
      console.error('Mark all as read error:', error);
    }
  },

  /**
   * Send email notification via Resend
   */
  async sendEmail(to, subject, message, emailType = 'notification') {
    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured');
      }

      // The edge function now requires an authenticated caller (not the public
      // anon key) — send the current user's access token.
      const { data: { session } } = await supabase?.auth?.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        // No authenticated session → skip silently; email is non-critical.
        console.warn('sendEmail skipped: no authenticated session');
        return null;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env?.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          to,
          subject,
          message,
          emailType
        })
      });

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Failed to send email');
      }

      const data = await response?.json();
      return data;
    } catch (error) {
      console.error('Send email error:', error);
      throw error;
    }
  },

  /**
   * Send reviewer assignment email.
   * Uses the conference's `reviewer_assignment` template (or built-in default).
   */
  async sendReviewerAssignmentEmail(reviewerEmail, reviewerName, paperTitle, conferenceTitle, conferenceId) {
    try {
      const reviewLink = `${window.location.origin}/reviewer-dashboard`;
      return await this.sendEventEmail(conferenceId, 'reviewer_assignment', reviewerEmail, {
        reviewerName,
        paperTitle,
        conferenceName: conferenceTitle,
        reviewLink
      }, 'assignment');
    } catch (error) {
      console.error('Send reviewer assignment email error:', error);
      throw error;
    }
  },

  /**
   * Send paper decision email to author.
   * Routes to the accepted / rejected / revision template based on the decision.
   */
  async sendDecisionEmail(authorEmail, authorName, paperTitle, decision, conferenceTitle, conferenceId) {
    try {
      const decisionText = {
        'accept': 'Accepted',
        'accepted': 'Accepted',
        'reject': 'Rejected',
        'rejected': 'Rejected',
        'minor_revision': 'Minor Revisions',
        'major_revision': 'Major Revisions',
        'revision_required': 'Revisions Required'
      }?.[decision] || decision;

      const isAccept = decision === 'accept' || decision === 'accepted';
      const isReject = decision === 'reject' || decision === 'rejected';
      const eventKey = isAccept ? 'paper_accepted' : isReject ? 'paper_rejected' : 'revision_required';

      return await this.sendEventEmail(conferenceId, eventKey, authorEmail, {
        authorName,
        paperTitle,
        conferenceName: conferenceTitle,
        decision: decisionText
      }, 'decision');
    } catch (error) {
      console.error('Send decision email error:', error);
      throw error;
    }
  },

  /**
   * Send review submission confirmation email to reviewer.
   */
  async sendReviewSubmittedEmail(reviewerEmail, reviewerName, paperTitle, conferenceTitle, conferenceId) {
    try {
      const reviewLink = `${window.location.origin}/reviewer-dashboard`;
      return await this.sendEventEmail(conferenceId, 'review_submitted', reviewerEmail, {
        reviewerName,
        paperTitle,
        conferenceName: conferenceTitle,
        reviewLink
      }, 'review_confirmation');
    } catch (error) {
      console.error('Send review submitted email error:', error);
      // Don't throw — email failure shouldn't break the review submission
    }
  },

  /**
   * Send first paper submission confirmation email to author.
   */
  async sendSubmissionConfirmationEmail(authorEmail, authorName, paperTitle, conferenceTitle, participantId, conferenceId) {
    try {
      return await this.sendEventEmail(conferenceId, 'submission_received', authorEmail, {
        authorName,
        paperTitle,
        conferenceName: conferenceTitle,
        participantId: participantId || ''
      }, 'submission_confirmation');
    } catch (error) {
      console.error('Send submission confirmation email error:', error);
      throw error;
    }
  },

  /**
   * Send camera-ready confirmation email to author.
   */
  async sendCameraReadyConfirmationEmail(authorEmail, authorName, paperTitle, conferenceTitle, conferenceId) {
    try {
      return await this.sendEventEmail(conferenceId, 'camera_ready_received', authorEmail, {
        authorName,
        paperTitle,
        conferenceName: conferenceTitle
      }, 'status_update');
    } catch (error) {
      console.error('Send camera-ready confirmation email error:', error);
      throw error;
    }
  },

  /**
   * Send camera-ready upload notification to conference chairs.
   */
  async sendCameraReadyChairNotification(chairEmail, chairName, paperTitle, authorName, conferenceTitle, conferenceId) {
    try {
      return await this.sendEventEmail(conferenceId, 'camera_ready_chair', chairEmail, {
        chairName,
        paperTitle,
        authorName,
        conferenceName: conferenceTitle
      }, 'status_update');
    } catch (error) {
      console.error('Send camera-ready chair notification error:', error);
      throw error;
    }
  }
};

export default notificationService;
